import { useRouter } from 'next/router';
import { type ReactNode, useEffect, useRef } from 'react';

import {
  trackPublicRetrofitBucketAssigned,
  trackPublicRetrofitOutOfService,
} from '@/components/BatteryEcomm/PublicRetrofit/analytics';
import useGlobalStore from '@/data-store/zustand/hooks/useGlobalStore';
import useEcommMarket from '@/hooks/geo';
import {
  getOrPersistFirstLookOverride,
  getPublicRetrofitRolloutAssignment,
  TEST_OVERRIDE_QUERY_PARAM,
} from '@/utils/featureFlags/publicRetrofitRollout';
import {
  isPublicBatteryRoute,
  PUBLIC_BATTERY_ECOMM_ROUTES,
} from '@/utils/publicBatteryEcomm/pageUrls';

export default function PublicRetrofitRolloutGate({
  children,
}: {
  children: ReactNode;
}) {
  const { pathname, query, isReady } = useRouter();
  const { featureFlags } = useGlobalStore('featureFlags');
  const { userLocation, isLocationPending } = useEcommMarket();
  const { state } = userLocation;
  const hasTrackedAssignment = useRef(false);

  const isBatteryRoute = isPublicBatteryRoute(pathname);
  // Flags, geo state, and the query string (which carries the first-look
  // bypass) resolve after mount — withhold battery content until all are
  // known so excluded visitors never see the page flash
  const isPending = featureFlags === undefined || isLocationPending || !isReady;
  const includeInRollout = getOrPersistFirstLookOverride(
    query[TEST_OVERRIDE_QUERY_PARAM]
  );
  const { inRollout, rolloutPercent, bucket } =
    getPublicRetrofitRolloutAssignment(featureFlags, state, includeInRollout);
  const enterFlow = !isPending && inRollout;

  // Reports the excluded arm too, before the redirect runs, so both arms are
  // comparable in GTM
  useEffect(() => {
    if (isPending || !isBatteryRoute || state === null) return;
    if (hasTrackedAssignment.current) return;

    hasTrackedAssignment.current = true;
    trackPublicRetrofitBucketAssigned({
      bucket,
      rolloutPercent,
      state,
      inRollout,
    });
    if (!inRollout) {
      trackPublicRetrofitOutOfService({
        reason: 'not_in_market_rollout',
        state,
      });
    }
  }, [bucket, inRollout, isBatteryRoute, isPending, rolloutPercent, state]);

  useEffect(() => {
    if (isBatteryRoute && !isPending && !enterFlow) {
      window.location.replace(PUBLIC_BATTERY_ECOMM_ROUTES.ADD_A_BATTERY);
    }
  }, [isBatteryRoute, isPending, enterFlow]);

  if (!isBatteryRoute) return children;
  if (!enterFlow) return null;
  return children;
}
